import React, { useRef, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useDrag, useDrop } from 'react-dnd';
import { EditOutlined } from '@ant-design/icons';
import { DateTime } from 'luxon';
import { Col, Row } from 'antd';

import BorderlessButton from '../common/buttons/BorderlessButton';

import { getAllDayValues } from '../helpers/time';

export default function DraggableCategoryShift({
  shift,
  index,
  onMoveRow,
  category,
  isDraggable = false,
  onShiftSelect,
}) {
  const ref = useRef();
  const [{ isOver, dropClassName }, drop] = useDrop({
    accept: 'schedule',
    collect: (monitor) => {
      const { id: dragId, index: dragIndex, shiftCategoryId } = monitor.getItem() || {};
      if (dragId === shift.id) {
        return {};
      }

      return {
        isOver: monitor.isOver(),
        dropClassName: (
          dragIndex < index || category.id !== shiftCategoryId
            ? ' drop-over-downward'
            : ' drop-over-upward'
        ),
      };
    },
    drop: (item) => {
      onMoveRow({
        oldCategoryId: item.shiftCategoryId,
        newCategoryId: category.id,
        oldId: item.id,
        newId: shift.id,
      });
    },
    canDrop: () => true,
  }, [shift, onMoveRow, isDraggable, category]);
  const [, drag] = useDrag({
    type: 'schedule',
    item: { ...shift, index },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
    canDrag: () => isDraggable,
  }, [shift, isDraggable]);
  drop(drag(ref));

  const baseStyle = { cursor: isDraggable ? 'move' : 'inherit' };

  const style = !isDraggable
    ? { fontStyle: 'italic', paddingLeft: 10, ...baseStyle }
    : baseStyle;

  const formattedTime = useMemo(() => {
    if (!shift.startTime && !shift.endTime) return '';
    let startDT = shift.startTime
      ? DateTime.fromMillis(shift.startTime)
      : null;
    let endDT = shift.endTime
      ? DateTime.fromMillis(shift.endTime)
      : null;
    const startInShiftTimezone = shift.startTime
      ? DateTime.fromMillis(shift.startTime, { zone: shift.timezone })
      : null;
    const endInShiftTimezone = shift.endTime
      ? DateTime.fromMillis(shift.endTime, { zone: shift.timezone })
      : null;
    const {
      isAllDay,
    } = getAllDayValues(
      startInShiftTimezone,
      endInShiftTimezone,
    );
    if (isAllDay) {
      startDT = startInShiftTimezone;
      endDT = endInShiftTimezone;
    }
    const format = isAllDay
      ? DateTime.DATE_SHORT
      : DateTime.DATETIME_SHORT;
    const startTime = startDT
      ? startDT.toLocaleString(format)
      : '';
    const endTime = shift.endTime
      ? endDT.toLocaleString(format)
      : '';
    const connector = startTime && endTime ? ' - ' : '';
    return `${startTime}${connector}${endTime}`;
  }, [shift]);

  return (
    <div
      ref={ref}
      key={shift.id}
      className={`schedule-category-shift-name${isOver ? dropClassName : ''}`}
      style={style}
    >
      <Row>
        <Col span={24}>
          <div className="schedule-category-shift-title-container">
            <div className="schedule-category-shift-title">{shift.title}</div>
            {!!isDraggable && (
              <BorderlessButton
                iconNode={<EditOutlined />}
                onClick={() => onShiftSelect(shift, true)}
                className="schedule-category-shift-edit-btn"
              />
            )}
          </div>
        </Col>
        { !!formattedTime && (
          <Col span={24} style={{ fontSize: 10 }}>
            {formattedTime}
          </Col>
        )}
      </Row>
    </div>
  );
}

DraggableCategoryShift.propTypes = {
  shift: PropTypes.shape({
    id: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    startTime: PropTypes.number,
    endTime: PropTypes.number,
    timezone: PropTypes.string,
  }).isRequired,
  index: PropTypes.number.isRequired,
  onMoveRow: PropTypes.func.isRequired,
  category: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    color: PropTypes.string,
  }).isRequired,
  isDraggable: PropTypes.bool,
  onShiftSelect: PropTypes.func.isRequired,
};

DraggableCategoryShift.defaultProps = {
  isDraggable: false,
};
